import { useEffect, useReducer, useRef } from 'react';
import { isSameCoachSnapshot } from './collect';
import { pickCoachFromReport } from './excel';
import { SessionReport, SessionReportResult } from './types';

/**
 * 화면 혼합 방지 상태.
 * - `none`: 요약과 상세가 같은 계산이거나 아직 비교할 수 없음(받는 중 등)
 * - `refreshed`: 달라서 요약을 다시 받았고, 다시 받은 그 요약이 지금도 상세와 같다
 * - `mismatch`: 다시 받기를 해 봤는데 실패했거나 여전히 다르다
 */
export type CoachSummarySyncState = 'none' | 'refreshed' | 'mismatch';

type RefetchResult = { isSuccess: boolean; data?: SessionReportResult; dataUpdatedAt?: number };

/**
 * 고른 강사의 상세 응답과 캐시된 요약의 그 강사 줄을 **렌더마다 다시 비교**한다(비교는 건너뛰지 않는다).
 * 다르면 요약을 다시 받는데, 이 자동 재조회만 「강사 + 기준 + 상세 응답」당 1회로 제한한다.
 * 상태는 강사·기준별로 따로 들고 있어, 다른 강사를 보고 돌아와도 그 강사의 불일치 경고가 유지된다.
 * `refreshed` 는 재조회로 받은 그 요약이 화면에 있는 동안만 — 요약이 또 바뀌면 다시 비교 결과를 따른다.
 */
export const useCoachSummarySync = ({
    summaryReport,
    coachReport,
    selectedCoachId,
    isFetching,
    summaryUpdatedAt,
    detailUpdatedAt,
    refetchSummary,
    resetKey,
}: {
    summaryReport?: SessionReport;
    coachReport?: SessionReport;
    selectedCoachId: string;
    /** 요약·상세 중 하나라도 받는 중 */
    isFetching: boolean;
    summaryUpdatedAt: number;
    detailUpdatedAt: number;
    refetchSummary: () => Promise<RefetchResult>;
    /** 센터·달·유령 기준 */
    resetKey: string;
}): CoachSummarySyncState => {
    const [, rerender] = useReducer((x: number) => x + 1, 0);
    const attempted = useRef(new Set<string>());
    const inFlight = useRef(new Set<string>());
    /** 강사·기준별: 재조회로 맞춰진 요약의 dataUpdatedAt */
    const refreshedAt = useRef(new Map<string, number>());
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const scope = `${resetKey}|${selectedCoachId}`;
    const attemptKey = `${scope}|${detailUpdatedAt}`;
    const detail = coachReport && selectedCoachId ? pickCoachFromReport(coachReport, selectedCoachId) : undefined;
    const inSummary = summaryReport?.coaches.find((c) => c.coachId === selectedCoachId);
    const comparable =
        !isFetching &&
        !!detail &&
        !!inSummary &&
        coachReport?.ghostDays === summaryReport?.ghostDays &&
        coachReport?.yearMonth === summaryReport?.yearMonth;
    const mismatch = comparable ? !isSameCoachSnapshot(inSummary!, detail!) : null;

    useEffect(() => {
        if (mismatch !== true || !detail) return;
        if (attempted.current.has(attemptKey)) return;
        attempted.current.add(attemptKey);
        inFlight.current.add(attemptKey);
        refetchSummary()
            .then((r) => {
                const fresh = r.isSuccess && r.data?.status === 'ok' ? r.data.report : undefined;
                const freshCoach = fresh?.coaches.find((c) => c.coachId === selectedCoachId);
                if (freshCoach && isSameCoachSnapshot(freshCoach, detail) && r.dataUpdatedAt) {
                    refreshedAt.current.set(scope, r.dataUpdatedAt);
                }
            })
            .catch(() => undefined)
            .finally(() => {
                inFlight.current.delete(attemptKey);
                if (mounted.current) rerender();
            });
    }, [mismatch, attemptKey, scope, detail, selectedCoachId, refetchSummary]);

    if (mismatch === true) {
        return attempted.current.has(attemptKey) && !inFlight.current.has(attemptKey) ? 'mismatch' : 'none';
    }
    if (mismatch === false && refreshedAt.current.get(scope) === summaryUpdatedAt) return 'refreshed';
    return 'none';
};
